import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';

export default function LCL() {
  return (
    <>
      <SEO
        title="LCL - Containerfragt som stykgods - Hvornår skal man vælge LCL?"
        description="Har du gods det ikke kan fylde en hel container? Så tilbyder vi en smart, billig og sikker løsning, nemlig LCL også kendt som stykgods. Læs mere her"
        canonicalUrl="https://fastlane.dk/lcl-less-container-load"
      />

      <PageHeader
        title="LCL - Less than Container Load"
        subtitle="Containerfragt som stykgods – del pladsen og spar omkostninger ved mindre forsendelser."
        breadcrumbs={[
          { label: 'Ydelser', path: '/godstransport-i-europa' },
          { label: 'Søfragt', path: '/containerfragt-soefragt' },
          { label: 'LCL Stykgods' }
        ]}
      />

      <div className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {/* Section 1 */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-base sm:text-lg">
            <h2 className="text-2xl font-bold text-[#0f2b48]">
              LCL - Containerfragt som stykgods
            </h2>
            <p>
              Er din forsendelse ikke stor nok til at fylde en hel container tilbyder Fastlane at samsende med andet gods, hvilket typisk er en billigere fragtløsning ved mindre forsendelser.
            </p>
            <p>
              Fastlane er en dansk logistikvirksomhed, der arrangerer containerfragt som LCL (Less than Container Load) også kendt som stykgods i det meste af verden. LCL eller stykgods er en fragtløsning, hvor du deler pladsen i en container med.andre transportkøbere fremfor at bestille en{' '}
              <Link to="/containerfragt-soefragt" className="text-sky-700 font-semibold underline">
                hel container FCL (Full Container Load)
              </Link>
              . Stykgods kan eksempelvis være en paller eller en kasse som skal sendes. De gængse containertyper er 20, 40 fods DC – Dry Container eller 40 fods HC - High Cube Dry Container.
            </p>
          </div>

          {/* Section 2: Hvornår bør man vælge LCL */}
          <div className="bg-slate-50 p-6 sm:p-8 rounded-2xl border border-slate-200/90 space-y-4">
            <h3 className="text-xl font-bold text-[#0f2b48]">
              Hvornår bør man vælge LCL?
            </h3>
            <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
              Man bør vælge LCL som fragtløsning, hvis man har en mindre forsendelse, så en hel container ikke giver mening. Der kan være forskellige fordele ved at vælge LCL som fragtløsning. Økonomisk vil der typisk være noget at spare. Samtidig er det et mere miljøvenligt valg, idet pladsen bliver bedre udnyttet.
            </p>
            <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
              Du behøver ikke bekymre dig om, at finde nogen at dele containeren med - det sørger vi for. Alt du skal gøre er, at booke en LCL leverance og give nødvendige informationer om dit gods til os.
            </p>
            <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
              Er du i tvivl om dit gods egner sig til LCL containerfragt? Så kontakt os, så vi kan hjælpe dig med hvilken løsning der egner sig bedst til dit gods.
            </p>
            <p className="text-sm font-semibold text-slate-900">
              Vi tilbyder også FCL fragtløsninger i det meste af verden.
            </p>
          </div>

          {/* Section 3: Priser */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base border-t border-slate-200 pt-8">
            <h2 className="text-2xl font-bold text-[#0f2b48]">Priser på LCL containerfragt</h2>
            <p>
              For at kunne fastsætte den præcis pris på hvad en LCL fragtløsning kommer til at koste, skal vi bruge nogle oplysninger. Det er vigtigt at kende til den mængde af gods, der skal transporteres, og for at kunne definere containerfragten, samt vælge skibsforbindelsen, skal vi kende afsender - og modtager adressen. Den endelige rute vælges under hensyntagen til transporttid og pris, så løsningen matcher dit fragtbehov og forventninger.
            </p>

            <h4 className="font-bold text-slate-900 pt-2">Typiske nødvendige oplysninger for at få prisen angivet:</h4>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
              <li className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600" aria-hidden="true" /><span>Mængde af gods?</span></li>
              <li className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600" aria-hidden="true" /><span>Mål og vægt på godset?</span></li>
              <li className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600" aria-hidden="true" /><span>Hvor forsendelsen sendes fra</span></li>
              <li className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600" aria-hidden="true" /><span>Hvor forsendelsen skal hen?</span></li>
              <li className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600" aria-hidden="true" /><span>Hvornår den kan afhentes?</span></li>
              <li className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600" aria-hidden="true" /><span>Hvornår kan godset hentes eller indleveres?</span></li>
              <li className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600" aria-hidden="true" /><span>Har du brug for forsikring på din forsendelse?</span></li>
              <li className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600" aria-hidden="true" /><span>Hvilken leveringsbetingelse – Incoterms – gælder for fragten?</span></li>
            </ul>

            <div className="mt-8 p-6 bg-slate-50 rounded-xl border border-slate-200">
              <p className="font-semibold text-slate-900 mb-2">Nogle af de lande, som vi betjener:</p>
              <p className="text-xs text-slate-600 mb-4">
                Sydafrika, Kina, Indien, Pakistan, Sydkorea, Thailand, Vietnam, USA, Canada, Brasilien, Chile, Argentina, Japan, Malaysia, Indonesien, Danmark, Norge, Sverige og England.
              </p>
              <p className="font-semibold text-slate-900 mb-2">Nogle af de havnebyer vi sender via:</p>
              <p className="text-xs text-slate-600">
                Hamburg, Bremerhaven, Antwerpen, Rotterdam, Southampton, Shanghai, Ningbo, Hong Kong, Shenzhen, Guangzhou, Miami, New York, Toronto, Mumbai, Busan, Singapore, Karachi, Göteborg, Durban, Johannesburg, Cape Town og Port Elizabeth.
              </p>
            </div>
          </div>

          {/* Call to action */}
          <div className="p-6 bg-sky-50 rounded-2xl border border-sky-100 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-[#0f2b48]">Få et godt tilbud på LCL fragt hos Fastlane</h3>
              <p className="text-sm text-slate-600">Har du brug for at få fragtet gods der ikke fylder en hel container?</p>
            </div>
            <Link
              to="/fragtrater"
              className="inline-flex items-center px-5 py-2.5 rounded-lg bg-[#b25d19] text-white text-sm font-semibold hover:bg-[#974d12] whitespace-nowrap"
            >
              Få et tilbud
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
